/**
 * Phase D — thin wrappers around the AI Deal Advisor backend.
 *
 * All routes are auth-required (bearer injected by `apiClient`). The
 * AIAdvisorPanel imports these directly; tests can swap them out via
 * `jest.mock` if/when react-testing-library lands.
 */
import { apiClient, getAccessToken, refreshAccessToken } from "./client";
import i18n from "../i18n";

function uiLocale() {
  return (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];
}

/**
 * Fetch the canned quick-action chips for one vertical. Cached for
 * the session by the panel (catalog only changes when we ship).
 *
 * @param {"auto"|"immo"} vertical
 * @returns {Promise<{vertical: string, items: Array<{slug:string,label:string}>}>}
 */
export async function fetchQuickActions(vertical, country) {
  const params = { vertical };
  if (country) params.country = country;
  const { data } = await apiClient.get("/ai/quick-actions", { params });
  return data;
}

/**
 * Read-only lookup for the user's existing conversation on a listing.
 * Returns the conversation doc on success, `null` on 404 (no existing
 * thread — perfectly normal). Never triggers a Claude call.
 */
export async function fetchConversationByListing(listingUlid) {
  try {
    // P0.6 — never seed the panel with a differently-language chat.
    const { data } = await apiClient.get(
      `/ai/conversations/by-listing/${listingUlid}`,
      { params: { ui_locale: uiLocale() } },
    );
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Fire one advisor turn. The backend rate-limits to 30/hour/user;
 * 429 responses bubble up with `error.response.headers["retry-after"]`
 * and `error.response.data.error.message` (German).
 */
export async function sendAdvisorTurn({
  listingUlid,
  message,
  quickActionSlug,
  resetConversation,
  clientRequestId,
}) {
  const body = { listing_ulid: listingUlid, ui_locale: uiLocale() };
  if (quickActionSlug) body.quick_action_slug = quickActionSlug;
  if (message) body.message = message;
  if (resetConversation) body.reset_conversation = true;
  if (clientRequestId) body.client_request_id = clientRequestId;
  const { data } = await apiClient.post("/ai/deal-advisor", body);
  return data;
}

/**
 * P1.6 — Streaming variant of `sendAdvisorTurn`. Uses fetch +
 * ReadableStream (Bearer header requires POST → EventSource can't
 * carry it). Yields `{kind, ...}` events matching the backend
 * contract: start / delta / ping / complete / error.
 *
 * Callers pass an `onEvent` callback which fires for every event.
 * The returned promise resolves when the stream ends normally
 * (complete or error frame). A caller-provided `signal`
 * (AbortController) tears the stream down cleanly.
 *
 * P0.1 — Auth + Fallback contract (hardened after preview audit):
 *  - Bearer token comes from `getAccessToken()` (localStorage) — NOT
 *    from `apiClient.defaults.headers`, which is always empty because
 *    axios injects the token via a request interceptor.
 *  - A `401` triggers exactly ONE single-flight refresh via
 *    `refreshAccessToken()` and retries the request. If the refresh
 *    fails or the retry still 401s → throw (no legacy fallback).
 *  - `fetch` unavailable in the runtime BEFORE the request is sent
 *    → throw an `Error` with `.streamingUnavailable = true` so the
 *    caller MAY fall back to legacy (nothing was sent yet).
 *  - Any transport error thrown by `fetch` (`TypeError`, timeout,
 *    abort, mid-stream disconnect) → throw as-is. The caller MUST
 *    NOT auto-fallback because the request may have reached the
 *    server / Claude — a fallback would double-bill and violate
 *    rate limits. Retry is a deliberate UI decision guarded by the
 *    `client_request_id` idempotency key.
 *  - HTTP error status (not 401) → throw an `Error` with
 *    `.status = resp.status`. The caller must fall back ONLY on
 *    404 (feature flag off); every other status (402/403/429/500/…)
 *    is surfaced.
 *  - `error` SSE frames are surfaced via `onEvent` and end the
 *    stream — the caller must NOT retry (Claude was already billed).
 *
 * IMPORTANT: no automatic retry once a delta has been observed —
 * that would double-bill the Claude call and duplicate the assistant
 * message. Retries are the caller's decision, guarded by the
 * `client_request_id` idempotency key.
 */
export async function streamAdvisorTurn({
  listingUlid,
  message,
  quickActionSlug,
  resetConversation,
  clientRequestId,
  onEvent,
  signal,
}) {
  // P0.1 — surface fetch unavailability as `streamingUnavailable`
  // BEFORE any request is issued so the caller MAY fall back safely.
  if (typeof fetch !== "function") {
    throw Object.assign(
      new Error("streamAdvisorTurn: fetch unavailable"),
      { streamingUnavailable: true },
    );
  }

  const body = { listing_ulid: listingUlid, ui_locale: uiLocale() };
  if (quickActionSlug) body.quick_action_slug = quickActionSlug;
  if (message) body.message = message;
  if (resetConversation) body.reset_conversation = true;
  if (clientRequestId) body.client_request_id = clientRequestId;

  // apiClient uses axios — for streaming we need fetch. Base URL
  // comes from the same source of truth so any change propagates.
  // Token comes from getAccessToken() (localStorage), NOT from
  // `apiClient.defaults.headers` which is never populated
  // (interceptor sets Authorization per-request).
  const base = apiClient.defaults.baseURL || "";

  const buildHeaders = (rawToken) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (rawToken) headers.Authorization = `Bearer ${rawToken}`;
    return headers;
  };

  const doFetch = (token) =>
    fetch(`${base}/ai/deal-advisor/stream`, {
      method: "POST",
      headers: buildHeaders(token),
      body: JSON.stringify(body),
      signal,
    });

  let token = getAccessToken();
  let resp = await doFetch(token);

  // P0.1 — 401 → single-flight refresh + retry ONCE. No fallback.
  if (resp.status === 401) {
    try { resp.body?.cancel?.(); } catch { /* ignore */ }
    const fresh = await refreshAccessToken();
    if (!fresh) {
      throw Object.assign(
        new Error("streamAdvisorTurn HTTP 401 (refresh failed)"),
        { status: 401, refreshFailed: true },
      );
    }
    token = fresh;
    resp = await doFetch(token);
  }

  if (!resp.ok) {
    let detail = null;
    try {
      detail = await resp.json();
    } catch {
      /* ignore */
    }
    throw Object.assign(
      new Error(`streamAdvisorTurn HTTP ${resp.status}`),
      { status: resp.status, detail },
    );
  }
  if (!resp.body) {
    throw new Error("streamAdvisorTurn: no response body");
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line (\n\n). We ALWAYS
      // parse only whole frames; a partial frame at the tail stays
      // in the buffer for the next chunk.
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseSseFrame(frame);
        if (parsed) {
          try {
            onEvent(parsed);
          } catch (cbErr) {
            // Never let a UI callback tear down the stream.
            console.warn("streamAdvisorTurn onEvent threw", cbErr);
          }
          if (parsed.kind === "complete" || parsed.kind === "error") {
            return parsed;
          }
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* ignore */ }
  }
  return null;
}

function parseSseFrame(frame) {
  let eventName = "message";
  let dataLines = [];
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.trimEnd();
    if (!line) continue;
    if (line.startsWith(":")) continue; // comment
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^\s/, ""));
    }
  }
  if (!dataLines.length) return null;
  try {
    const data = JSON.parse(dataLines.join("\n"));
    return { kind: eventName, ...data };
  } catch {
    return null;
  }
}

/**
 * Admin-only. Aggregates AI token spend across the trailing N days.
 * Renders inside the panel's admin debug widget when
 * `user.role === "admin"`.
 */
export async function fetchAdminUsageSummary(days = 7) {
  const { data } = await apiClient.get("/ai/usage/summary", {
    params: { days },
  });
  return data;
}

// ---- V4 — AI Market Intelligence Assistant (Modus B) ------------------

export async function sendAssistantTurn({
  message,
  quickActionSlug,
  resetConversation,
  clientRequestId,
}) {
  const body = { ui_locale: uiLocale() };
  if (message) body.message = message;
  if (quickActionSlug) body.quick_action_slug = quickActionSlug;
  if (resetConversation) body.reset_conversation = true;
  if (clientRequestId) body.client_request_id = clientRequestId;
  const { data } = await apiClient.post("/ai/assistant", body);
  return data;
}

export async function fetchAssistantConversation() {
  // P0.6 — never load a differently-language market chat into the UI.
  const { data } = await apiClient.get("/ai/assistant/conversation", {
    params: { ui_locale: uiLocale() },
  });
  return data;
}

export async function fetchAiGreeting() {
  const { data } = await apiClient.get("/ai/memory");
  return data;
}

export async function fetchAssistantQuickActions() {
  const { data } = await apiClient.get("/ai/assistant/quick-actions");
  return data;
}

export async function fetchAssistantInsights() {
  const { data } = await apiClient.get("/ai/assistant/insights");
  return data;
}

// ---- V4.12 Phase F — persistent AI Canvas workspace --------------------

// ---- V4.14 — Market Brief (deltas since last visit) ---------------------

export async function fetchMarketBrief() {
  const { data } = await apiClient.get("/ai/market-brief");
  return data;
}

export async function ackMarketBrief() {
  const { data } = await apiClient.post("/ai/market-brief/ack");
  return data;
}

export async function fetchCanvas() {
  const { data } = await apiClient.get("/ai/canvas");
  return data;
}

export async function setCanvasWidgetPinned(widgetUlid, pinned) {
  const { data } = await apiClient.patch(`/ai/canvas/${widgetUlid}`, { pinned });
  return data;
}

export async function setCanvasWidgetCollapsed(widgetUlid, collapsed) {
  const { data } = await apiClient.patch(`/ai/canvas/${widgetUlid}`, { collapsed });
  return data;
}

// V4.23 — behavioral memory suggestions (confirm-first learning).
export async function confirmMemorySuggestion(key, value) {
  const { data } = await apiClient.post("/ai/memory/suggestions/confirm", { key, value });
  return data;
}

export async function dismissMemorySuggestion(key, value) {
  const { data } = await apiClient.post("/ai/memory/suggestions/dismiss", { key, value });
  return data;
}

export async function deleteCanvasWidget(widgetUlid) {
  const { data } = await apiClient.delete(`/ai/canvas/${widgetUlid}`);
  return data;
}

export async function clearCanvas() {
  const { data } = await apiClient.post("/ai/canvas/clear");
  return data;
}
